import { useState, useEffect } from 'react'
import AuthService from '../services/AuthService'

export default function Login({ onLogin }) {
  const [credentials, setCredentials] = useState({ username: '', password: '' })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [publicUsers, setPublicUsers] = useState([])

  useEffect(() => {
    fetch('/api/auth/public-users')
      .then(r => r.json())
      .then(d => { if (d.success) setPublicUsers(d.users) })
      .catch(() => {})
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const user = await AuthService.login(credentials.username, credentials.password)
      if (user) {
        onLogin(user)
      } else {
        setError('Identifiants incorrects')
      }
    } catch {
      setError('Erreur de connexion')
    } finally {
      setLoading(false)
    }
  }

  const handleDemoLogin = async (username, password) => {
    setCredentials({ username, password })
    setLoading(true)
    setError('')
    try {
      const user = await AuthService.login(username, password)
      if (user) onLogin(user)
    } catch {
      setError('Erreur de connexion')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: '#d1d6d8' }}>
      <div className="w-full max-w-sm">

        {/* Header */}
        <div className="mb-8 flex flex-col items-start">
          <h1 className="text-5xl font-extrabold text-[#2f3126] font-title tracking-tight">
            ReputAI
          </h1>
          <p className="text-[#77726e] mt-1 text-sm">
            Décryptez ce que les modèles disent de vous.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white border border-[#77726e]/30 shadow-md p-7">
          <h2 className="text-base font-bold text-[#2f3126] mb-5 uppercase tracking-wider">
            Connexion
          </h2>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md px-4 py-2 mb-4 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[#77726e] uppercase tracking-wider mb-1">
                Nom d'utilisateur
              </label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="w-full border border-[#77726e]/40 rounded-md px-3 py-2 text-sm text-[#2f3126] bg-[#f6f3ee] focus:outline-none focus:ring-2 focus:ring-[#4e6498]/30 focus:border-[#4e6498]"
                placeholder="Votre nom d'utilisateur"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#77726e] uppercase tracking-wider mb-1">
                Mot de passe
              </label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full border border-[#77726e]/40 rounded-md px-3 py-2 text-sm text-[#2f3126] bg-[#f6f3ee] focus:outline-none focus:ring-2 focus:ring-[#4e6498]/30 focus:border-[#4e6498]"
                placeholder="Votre mot de passe"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !credentials.username || !credentials.password}
              className="w-full bg-[#2f3126] text-[#f6f3ee] rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-[#77726e] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>

          {/* Accès rapide — comptes publics configurés par l'admin */}
          {publicUsers.length > 0 && (
            <div className="mt-6 pt-5 border-t border-[#77726e]/20">
              <p className="text-xs font-semibold text-[#77726e] uppercase tracking-wider mb-3">
                Accès rapide
              </p>
              <div className="flex flex-col gap-2">
                {publicUsers.map(({ username, company }) => (
                  <button
                    key={username}
                    onClick={async () => {
                      setLoading(true)
                      setError('')
                      try {
                        const user = await AuthService.publicLogin(username)
                        if (user) onLogin(user)
                        else setError('Accès refusé')
                      } catch {
                        setError('Erreur de connexion')
                      } finally {
                        setLoading(false)
                      }
                    }}
                    disabled={loading}
                    className="flex items-center justify-between w-full px-3 py-2 border border-[#77726e]/30 text-sm text-[#2f3126] bg-[#f6f3ee] hover:bg-[#d1d6d8] transition disabled:opacity-50 text-left"
                  >
                    <span className="font-semibold">{company || username}</span>
                    <span className="text-xs text-[#77726e]">Accès direct →</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Comptes de démo — dev only, tree-shaken out of production builds */}
          {import.meta.env.DEV && (
            <div className="mt-6 pt-5 border-t border-[#77726e]/20">
              <p className="text-xs font-semibold text-[#77726e] uppercase tracking-wider mb-3">
                Comptes de test
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Google', username: 'google', password: 'password123' },
                  { label: 'Admin', username: 'admin', password: 'admin123' },
                ].map(({ label, username, password }) => (
                  <button
                    key={label}
                    onClick={() => handleDemoLogin(username, password)}
                    disabled={loading}
                    className="px-3 py-1 border border-[#77726e]/40 text-xs text-[#2f3126] bg-[#f6f3ee] hover:bg-[#d1d6d8] transition disabled:opacity-50"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer logo */}
        <div className="mt-8 flex justify-center">
          <a href="https://opsci.ai" target="_blank" rel="noopener noreferrer">
            <img src="./opsci-logo-hg.png" alt="OpSci" className="h-6 opacity-60 hover:opacity-100 transition" />
          </a>
        </div>
      </div>
    </div>
  )
}
